<script lang="ts">
import type { LinkProps, NavigationMenuEmits } from '@nuxt/ui';
import type { FuseResultMatch } from 'fuse.js';
import type { CollapsibleRootProps } from 'reka-ui';
import type { Component } from 'vue';
import type { BadgeProps } from '#layers/ui/components/Badge.vue';
import type { SidebarAction } from '#layers/ui/components/Sidebar/Header/HeaderActions.vue';
import { pickLinkProps } from '@nuxt/ui/runtime/utils/link.js';
import { isNullishOrEmpty } from '#layers/base/public';

export interface SidebarContentItemEmits extends NavigationMenuEmits {
	hide: [id: SidebarItem['id']];
	show: [id: SidebarItem['id']];
}

export interface SidebarItem extends Omit<LinkProps, 'type' | 'raw' | 'custom'>, Pick<CollapsibleRootProps, 'defaultOpen' | 'open'> {
	id: string;
	children?: SidebarItem[];
	hideCollapse?: boolean;
	icon?: string;
	iconComponent?: Component;
	iconProps?: Record<string, unknown>;
	label?: string;
	hidden?: boolean;
	onSelect?: (e: Event) => void;
	slot?: string;
	value?: string;
	section?: boolean;
	active?: boolean;
	actions?: SidebarAction[];
	badge?: BadgeProps;
	/**
	 * Fuse.js search matches to highlight text in the item label.
	 */
	searchMatches?: ReadonlyArray<FuseResultMatch>;
	/**
	 * The key of the search matches to use for highlighting the item label.
	 *
	 * This should correspond to the key used in the Fuse.js search results for the item label, e.g. "title" or "name".
	 */
	searchMatchKey?: string;
	ui?: {
		wrapper?: string;
		label?: string;
		leadingIcon?: string;
	};
	[key: string]: any;
}

interface Props {
	item: SidebarItem;
	index: number;
	level?: number;
	collapsed?: boolean;
	editMode?: boolean;
}
</script>

<script setup lang="ts">
import { createReusableTemplate } from '@vueuse/core';
import { CollapsibleContent, CollapsibleRoot, CollapsibleTrigger, NavigationMenuItem, NavigationMenuLink } from 'reka-ui';
import { tv } from 'tailwind-variants';
import { computed, ref } from 'vue';

import { useRoute, useRouter } from '#app/composables/router';
import { useAppConfig } from '#app/config';
import { DLinkBase } from '#components';
import { includesParams } from '#layers/ui/utils/routing';

const props = withDefaults(defineProps<Props>(), {
	level: 0,
});

defineEmits<SidebarContentItemEmits>();

const isOpen = ref(true);

const appConfig = useAppConfig();

const contentItem = tv({
	slots: {
		sectionLabel: `
			group flex h-8 w-full items-center gap-1.5 rounded-md px-2 text-xs font-semibold text-muted uppercase
			focus:outline-none
			focus-visible:ring-1 focus-visible:ring-primary focus-visible:outline-none focus-visible:ring-inset
		`,
		itemTrailingIcon: `
			shrink-0 transform transition-transform duration-200
			group-data-[state=open]:rotate-180
			peer-data-[state=open]:rotate-180
		`,
		itemWrapper: `
			group relative flex h-8 w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium
			hover:bg-elevated
			focus:outline-none
			focus-visible:ring-1 focus-visible:ring-primary focus-visible:outline-none focus-visible:ring-inset
		`,
		itemLeadingIcon: 'size-4',
		itemLabel: 'truncate',
		itemExternalIcon: 'ms-auto size-3.5 shrink-0 text-dimmed',
		itemContent: 'flex flex-1 overflow-hidden',
		childList: 'm-0 mt-px space-y-px border-none',
		childItem: 'ms-0 rounded-md p-0',
	},
	variants: {
		active: {
			false: {
				itemWrapper: 'text-default hover:text-highlighted',
				itemLeadingIcon: 'text-toned',
			},
			true: {
				itemWrapper: 'bg-elevated hover:bg-elevated',
				itemLeadingIcon: 'text-toned',
				itemLabel: 'text-highlighted',
			},
		},
		disabled: {
			true: {
				itemWrapper: 'cursor-not-allowed opacity-75',
			},
		},
		collapsed: {
			true: {
				itemWrapper: 'justify-center px-1.5',
				itemLabel: 'hidden',
				itemContent: 'hidden',
				sectionLabel: 'hidden',
				itemTrailingIcon: 'hidden',
			},
		},
		label: {
			true: '',
		},
		hasActions: {
			true: {
				itemWrapper: 'pe-1',
			},
		},
	},
	compoundVariants: [{
		collapsed: true,
		class: {
			link: 'px-1.5',
		},
	}, {
		label: true,
		class: {
			childList: 'ms-0',
		},
	}],
});

const ui = computed(() => contentItem({
	collapsed: props.collapsed,
	active: isItemActive.value,
	disabled: !!props.item.disabled,
	hasActions: !isNullishOrEmpty(props.item.actions),
}));

const route = useRoute();
const router = useRouter();

const isItemActive = computed(() => {
	if (props.item.active) {
		return true;
	}

	if (props.item.to) {
		const itemRoute = router.resolve(props.item.to);
		const { matched } = itemRoute;
		const routeMatched = matched.at(-1);
		const currentMatched = route.matched;

		if (!routeMatched || !currentMatched) {
			return false;
		}

		const index = currentMatched.indexOf(routeMatched);

		return index !== -1 && includesParams(route.params, itemRoute.params);
	}

	return false;
});

function onItemClick(event: MouseEvent) {
	if (isNullishOrEmpty(props.item.children)) {
		return;
	}

	if (props.item.to === undefined) {
		isOpen.value = !isOpen.value;
		return;
	}

	event.stopPropagation();

	if (isOpen.value && isItemActive.value) {
		event.preventDefault();
		event.stopPropagation();
		return;
	}

	isOpen.value = true;
}

function toggleExpanded() {
	if (props.item.hideCollapse) {
		return;
	}

	isOpen.value = !isOpen.value ? true : !isOpen.value;
}

const isCollapsible = computed(() => !isNullishOrEmpty(props.item.children));

const [DefineEditToggle, ReuseEditToggle] = createReusableTemplate();
const [DefineCollapseToggle, ReuseCollapseToggle] = createReusableTemplate();
const [DefineItemLabel, ReuseItemLabel] = createReusableTemplate();
</script>

<template>
	<DefineEditToggle>
		<template v-if="editMode">
			<DButton
				v-if="!item.hidden"
				icon="lucide:eye-off"
				variant="link"
				color="neutral"
				class="
					-mx-1.5 hidden items-center text-muted
					group-focus-within:flex
					group-hover:flex
					group-data-active:text-highlighted
				"
				:ui="{
					leadingIcon: 'size-4 shrink-0',
				}"
				:disabled="item.disabled"
				@click.stop.prevent="$emit('hide', item.id)"
			/>
			<DButton
				v-else
				icon="lucide:eye"
				variant="link"
				color="neutral"
				class="-mx-1.5 flex items-center text-muted"
				:disabled="item.disabled"
				:ui="{
					leadingIcon: 'size-4 shrink-0',
				}"
				@click.stop.prevent="$emit('show', item.id)"
			/>
		</template>
	</DefineEditToggle>

	<DefineCollapseToggle>
		<DButton
			v-if="!isNullishOrEmpty(item.children) && !item.hideCollapse"
			variant="link"
			color="neutral"
			size="xs"
			:icon="appConfig.ui.icons.chevronDown"
			:class="ui.itemTrailingIcon()"
			@click.stop.prevent="toggleExpanded"
		/>
	</DefineCollapseToggle>

	<DefineItemLabel>
		<span :class="ui.itemLabel({ class: ['peer', item.ui?.label] })">
			<DSearchHighlightedText
				:value="item.label ?? ''"
				:search-matches="item.searchMatches"
				:search-match-key="item.searchMatchKey ?? 'label'"
			/>
		</span>
	</DefineItemLabel>

	<component
		:is="isCollapsible ? CollapsibleRoot : NavigationMenuItem"
		as="li"
		:open="isOpen"
		:default-open="item.defaultOpen"
		:value="item.value || String(index)"
		:unmount-on-hide="false"
		v-bind="$attrs"
	>
		<DContextMenu
			v-if="item.section"
			:items="item.actions"
			:disabled="isNullishOrEmpty(item.actions)"
		>
			<component
				:is="isCollapsible && !item.hideCollapse ? CollapsibleTrigger : 'span'"
				:class="ui.sectionLabel({ class: [item.class] })"
			>
				<ReuseEditToggle />
				<div class="flex flex-1 overflow-hidden">
					<div class="flex flex-1 items-center overflow-hidden">
						<ReuseItemLabel
							@click="toggleExpanded"
						/>
						<CollapsibleTrigger
							v-if="isCollapsible && !isNullishOrEmpty(item.children)"
							as-child
						>
							<ReuseCollapseToggle />
						</CollapsibleTrigger>
					</div>
					<DSidebarContentItemActions
						v-if="!isNullishOrEmpty(item.actions)"
						:actions="item.actions"
						:active="isItemActive"
					/>
				</div>
			</component>
		</DContextMenu>
		<DLink
			v-else
			v-slot="{ active, ...slotProps }"
			v-bind="pickLinkProps(item as Omit<SidebarItem, 'type'>)"
			custom
		>
			<DContextMenu
				:items="item.actions"
				:disabled="isNullishOrEmpty(item.actions)"
			>
				<component :is="isCollapsible ? CollapsibleTrigger : 'span'" as-child>
					<NavigationMenuLink
						as-child
						:active="active || item.active"
						:disabled="item.disabled"
						@select="item.onSelect"
						@click="onItemClick($event)"
					>
						<component
							:is="item.to ? DLinkBase : 'span'"
							v-bind="item.to ? slotProps : {}"
							:class="[
								ui.itemWrapper({ class: item.ui?.wrapper }),
								level > 1 ? `ps-[calc(var(--spacing)*2+var(--spacing)*7*var(--level))]` : '']"
							:style="`--level: ${level > 0 ? level - 1 : 0}`"
						>
							<component
								:is="item.iconComponent"
								v-if="item.iconComponent"
								v-bind="item.iconProps"
								:class="[editMode && 'flex group-focus-within:hidden group-hover:hidden', item.ui?.leadingIcon]"
							/>
							<DIconFrame
								v-else-if="item.icon"
								:name="item.icon"
								:class="ui.itemLeadingIcon({
									class: [editMode && 'flex group-hover:hidden group-focus-within:hidden', item.ui?.leadingIcon],
								})"
								spacing="none"
								:color="item.iconColor"
								variant="link"
							/>
							<ReuseEditToggle />
							<div :class="ui.itemContent()">
								<div class="flex flex-1 items-center gap-1.5 overflow-hidden">
									<ReuseItemLabel />
									<DBadge v-if="item.badge" v-bind="item.badge" class="ml-auto shrink-0" size="xs" />
									<DIcon v-if="item.target === '_blank'" name="lucide:arrow-up-right" :class="ui.itemExternalIcon()" data-test="external-link-icon" />
									<ReuseCollapseToggle />
								</div>
								<DSidebarContentItemActions
									v-if="!isNullishOrEmpty(item.actions)"
									:actions="item.actions"
									:active="isItemActive"
								/>
							</div>
						</component>
					</NavigationMenuLink>
				</component>
			</DContextMenu>
		</DLink>
		<CollapsibleContent
			v-if="isCollapsible"
			class="
				overflow-hidden
				data-[state=closed]:animate-[collapsible-up_200ms_ease-out]
				data-[state=open]:animate-[collapsible-down_200ms_ease-out]
			"
		>
			<ul :class="ui.childList({ label: !item.to })">
				<DSidebarContentItem
					v-for="(childItem, childIndex) in item.children"
					:key="childItem.id"
					:index="childIndex"
					:level="level + 1"
					:class="ui.childItem()"
					:collapsed
					:edit-mode
					:item="childItem"
					@hide="(id: SidebarItem['id']) => $emit('hide', id)"
					@show="(id: SidebarItem['id']) => $emit('show', id)"
				/>
			</ul>
		</CollapsibleContent>
	</component>
</template>
